
/**
 * Module dependencies.
 */

import { Caption, resolveImageUrl } from 'slyk-web-libs/design-system';
import { Product } from 'slyk-web-libs/types';
import { camelCase, isEmpty } from 'lodash';
import { color, media } from 'react-components/styles';
import { unitsOfAccount } from 'client/core/constants/product';
import { useTranslate } from 'slyk-web-libs/i18n';
import React from 'react';
import logoPlaceholder from 'client/assets/images/mobile-app-preview/logo-placeholder.webp';
import noImage from 'client/assets/images/no-image/product@2x.webp';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  description: string | null | undefined;
  logoUrl: string | null | undefined;
  name: string | null | undefined;
  products: Array<Product & {
    displayPrice: string;
  }>;
};

/**
 * Unit of account types.
 */

const unitOfAccountTypes = Object.keys(unitsOfAccount);

/**
 * `SvgWrapper` styled component.
 */

const SvgWrapper = styled.div`
  bottom: 4px;
  left: 4px;
  position: absolute;
  right: 4px;
  top: 4px;

  ${media.min('ms')`
    bottom: 8px;
    left: 8px;
    right: 8px;
    top: 8px;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Caption).attrs({ as: 'p', size: 'medium' })`
  color: ${color('white')};
  font-size: 18px;
  line-height: 24px;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * `Name` styled component.
 */

const Name = styled(Caption).attrs({ as: 'p', size: 'small' })`
  color: ${color('white')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * `ProductInfo` styled component.
 */

const ProductInfo = styled(Caption).attrs({ as: 'p', size: 'extraSmall' })`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * `ProductAdditionalInfo` styled component.
 */

const ProductAdditionalInfo = styled.span`
  margin-left: 3px;
`;

/**
 * `CheckoutLabel` styled component.
 */

const CheckoutLabel = styled(Caption).attrs({ size: 'extraSmall' })`
  color: #59677c;
  display: inline-block;
`;

/**
 * Export `ProductsPreview` component.
 */

export function ProductsPreview(props: Props): JSX.Element {
  const { description, logoUrl, name, products } = props;
  const { translate } = useTranslate();
  const firstProduct = products?.[0] ?? {};
  const secondProduct = products?.[1] ?? {};

  return (
    <SvgWrapper>
      <svg
        fill={'none'}
        viewBox={'0 0 307 662'}
      >
        <defs>
          <clipPath id={'prefix__a'}>
            <path
              d={'M266.674 166.378c-10.489-14.639-49.947-14.631-60.423 0q-5.06 7.055-5.059 24.259t5.059 24.259c10.14 14.15 50.016 15.1 60.423.579q5.052-7.06 5.059-24.838 0-17.205-5.059-24.259z'}
              fill={'none'}
            />
          </clipPath>

          <clipPath id={'prefix__b'}>
            <rect
              fill={'none'}
              height={134}
              rx={3}
              width={134}
              x={13}
              y={280}
            />
          </clipPath>

          <clipPath id={'prefix__c'}>
            <rect
              fill={'none'}
              height={134}
              rx={3}
              width={134}
              x={159}
              y={280}
            />
          </clipPath>

          <pattern
            height={'1'}
            id={'pattern2'}
            patternContentUnits={'objectBoundingBox'}
            width={'1'}
          >
            <use transform={'scale(0.000133333 9.09091e-05)'} />
          </pattern>

          <pattern
            height={'1'}
            id={'pattern3'}
            patternContentUnits={'objectBoundingBox'}
            width={'1'}
          >
            <use transform={'scale(0.000133333 9.09091e-05)'} />
          </pattern>

          <pattern
            height={'1'}
            id={'pattern4'}
            patternContentUnits={'objectBoundingBox'}
            width={'1'}
          >
            <use transform={'scale(0.00106724 0.000717875)'} />
          </pattern>

          <pattern
            height={'1'}
            id={'pattern5'}
            patternContentUnits={'objectBoundingBox'}
            width={'1'}
          >
            <use transform={'scale(0.00106724 0.000717875)'} />
          </pattern>

          <filter
            colorInterpolationFilters={'sRGB'}
            filterUnits={'userSpaceOnUse'}
            height={'68'}
            id={'filter_1'}
            width={'319.348'}
            x={'-6'}
            y={'605'}
          >
            <feFlood
              floodOpacity={'0'}
              result={'BackgroundImageFix'}
            />

            <feColorMatrix
              in={'SourceAlpha'}
              result={'hardAlpha'}
              type={'matrix'}
              values={'0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'}
            />

            <feMorphology
              in={'SourceAlpha'}
              operator={'dilate'}
              radius={'1'}
              result={'effect1_dropShadow_1200_53683'}
            />

            <feOffset dy={'-1'} />

            <feGaussianBlur stdDeviation={'2.5'} />

            <feColorMatrix
              type={'matrix'}
              values={'0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0'}
            />

            <feBlend
              in2={'BackgroundImageFix'}
              mode={'normal'}
              result={'effect1_dropShadow_1200_53683'}
            />

            <feColorMatrix
              in={'SourceAlpha'}
              result={'hardAlpha'}
              type={'matrix'}
              values={'0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'}
            />

            <feOffset dy={'-1'} />

            <feGaussianBlur stdDeviation={'0.5'} />

            <feColorMatrix
              type={'matrix'}
              values={'0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.09 0'}
            />

            <feBlend
              in2={'effect1_dropShadow_1200_53683'}
              mode={'normal'}
              result={'effect2_dropShadow_1200_53683'}
            />

            <feBlend
              in={'SourceGraphic'}
              in2={'effect2_dropShadow_1200_53683'}
              mode={'normal'}
              result={'shape'}
            />
          </filter>

          <linearGradient
            gradientUnits={'userSpaceOnUse'}
            id={'linear_gradient_1'}
            x1={'153.679'}
            x2={'153.679'}
            y1={'619.498'}
            y2={'582.108'}
          >
            <stop
              offset={'0.11'}
              stopColor={'white'}
            />

            <stop
              offset={'0.3'}
              stopColor={'white'}
              stopOpacity={'0.71'}
            />

            <stop
              offset={'0.53'}
              stopColor={'white'}
              stopOpacity={'0.41'}
            />

            <stop
              offset={'0.72'}
              stopColor={'white'}
              stopOpacity={'0.19'}
            />

            <stop
              offset={'0.86'}
              stopColor={'white'}
              stopOpacity={'0.05'}
            />

            <stop
              offset={'0.93'}
              stopColor={'white'}
              stopOpacity={'0'}
            />
          </linearGradient>

          <linearGradient
            gradientUnits={'userSpaceOnUse'}
            id={'linear_gradient_2'}
            x1={'65.3285'}
            x2={'65.3285'}
            y1={'62.5216'}
            y2={'149.388'}
          >
            <stop stopColor={'#122744'} />

            <stop
              offset={'1'}
              stopColor={'#122744'}
              stopOpacity={'0.01'}
            />
          </linearGradient>
        </defs>

        <mask
          height={'662'}
          id={'mask0_1200_53683'}
          maskUnits={'userSpaceOnUse'}
          width={'307'}
          x={'0'}
          y={'0'}
        >
          <rect fill={'#FAFBFB'}
            height={'661.25'}
            rx={'40'}
            width={'307'}
          />
        </mask>

        <g mask={'url(#mask0_1200_53683)'}>
          <rect fill={'#FAFBFB'}
            height={'665'}
            width={'306.344'}
            y={'-2'}
          />

          {!isEmpty(products) && (
            <>
              <foreignObject
                height={18}
                width={114}
                x={'13'}
                y={'247.5'}
              >
                <Caption
                  color={'#b7bec6'}
                  size={'extraSmall'}
                >
                  {'Featured Products'}
                </Caption>
              </foreignObject>

              <foreignObject
                height={18}
                width={114}
                x={'235'}
                y={'247.5'}
              >
                <Caption
                  color={color('primary')}
                  size={'extraSmall'}
                >
                  {'View all'}
                </Caption>
              </foreignObject>

              <path
                d={'M285.546 255.434L288.416 258.14C288.435 258.158 288.453 258.176 288.472 258.194C289.391 259.12 289.382 260.612 288.453 261.526L285.546 264.387'}
                stroke={'#3366FF'}
                strokeWidth={'1.5'}
              />
            </>
          )}

          {!isEmpty(products) && (
            <>
              <path
                d={'M155.731 465.886C154.88 464.704 151.679 464.705 150.829 465.886C150.556 466.266 150.419 466.919 150.419 467.846C150.419 468.772 150.556 469.425 150.829 469.805C151.652 470.948 154.887 471.025 155.731 469.852C156.004 469.472 156.141 468.803 156.141 467.846C156.141 466.919 156.004 466.266 155.731 465.886Z'}
                fill={'#DCDFE3'}
                opacity={'0.5'}
              />

              <path
                d={'M145.922 465.886C145.07 464.704 141.869 464.705 141.02 465.886C140.746 466.266 140.609 466.919 140.609 467.846C140.609 468.772 140.746 469.425 141.02 469.805C141.842 470.948 145.077 471.025 145.922 469.852C146.195 469.472 146.332 468.803 146.332 467.846C146.332 466.919 146.195 466.266 145.922 465.886Z'}
                fill={'#3366FF'}
              />

              <path
                d={'M165.542 465.886C164.69 464.704 161.49 464.705 160.64 465.886C160.366 466.266 160.229 466.919 160.229 467.846C160.229 468.772 160.366 469.425 160.64 469.805C161.462 470.948 164.697 471.025 165.542 469.852C165.815 469.472 165.952 468.803 165.952 467.846C165.952 466.919 165.815 466.266 165.542 465.886Z'}
                fill={'#DCDFE3'}
                opacity={'0.5'}
              />
            </>
          )}

          <rect fill={'#122744'}
            height={'170.108'}
            width={'306.562'}
            y={'62.2988'}
          />

          <mask
            height={'171'}
            id={'mask1_1200_53683'}
            maskUnits={'userSpaceOnUse'}
            width={'307'}
            x={'0'}
            y={'62'}
          >
            <rect
              fill={'white'}
              height={'170.108'}
              width={'306.562'}
              y={'62.2988'}
            />
          </mask>

          <g mask={'url(#mask1_1200_53683)'}>
            <rect
              fill={'url(#pattern2)'}
              height={'545.321'}
              width={'373.371'}
              x={'-40.0576'}
              y={'-130.598'}
            />

            <mask
              height={'546'}
              id={'mask2_1200_53683'}
              maskUnits={'userSpaceOnUse'}
              width={'375'}
              x={'-41'}
              y={'-131'}
            >
              <rect
                fill={'url(#pattern3)'}
                height={'545.321'}
                width={'373.371'}
                x={'-40.0576'}
                y={'-130.598'}
              />
            </mask>

            <g mask={'url(#mask2_1200_53683)'}>
              <rect
                fill={'#122744'}
                fillOpacity={'0.9'}
                height={'545.321'}
                width={'373.371'}
                x={'-40.0576'}
                y={'-130.598'}
              />
            </g>

            <rect
              fill={'url(#pattern4)'}
              height={'513.044'}
              width={'346.619'}
              x={'-27.7964'}
              y={'-188.385'}
            />

            <mask
              height={'514'}
              id={'mask3_1200_53683'}
              maskUnits={'userSpaceOnUse'}
              width={'347'}
              x={'-28'}
              y={'-189'}
            >
              <rect
                fill={'url(#pattern5)'}
                height={'513.044'}
                width={'346.619'}
                x={'-27.7964'}
                y={'-188.385'}
              />
            </mask>

            <g mask={'url(#mask3_1200_53683)'}>
              <rect
                fill={'#122744'}
                fillOpacity={'0.6'}
                height={'513.044'}
                width={'346.619'}
                x={'-27.7964'}
                y={'-188.385'}
              />
            </g>
          </g>

          <rect
            fill={'#122744'}
            height={'65.1129'}
            width={'306.562'}
            y={'-2'}
          />

          <rect
            fill={'url(#linear_gradient_2)'}
            height={'151.388'}
            width={'306.562'}
            y={'-2'}
          />

          <foreignObject
            height={20}
            width={185}
            x={43}
            y={26}
          >
            <Name>
              {name}
            </Name>
          </foreignObject>

          <path
            clipRule={'evenodd'}
            d={'M240.97 31.0795L241.714 30.219C242.317 29.5234 243.184 29.125 244.095 29.125H246.317H255.094C256.006 29.125 256.873 29.5234 257.475 30.219L258.22 31.0795C258.73 31.6683 259.011 32.4262 259.011 33.2112V34.3023V39.3749V40.7795C259.011 41.5645 258.73 42.3224 258.22 42.9112L257.475 43.7716C256.873 44.4666 256.006 44.8657 255.094 44.8657H253.424V47.1783C253.424 47.3423 253.339 47.4953 253.2 47.5796L252.825 47.808C252.686 47.893 252.513 47.8974 252.371 47.8197L246.929 44.8657H244.095C243.184 44.8657 242.317 44.4666 241.714 43.7716L240.97 42.9112C240.46 42.3224 240.179 41.5645 240.179 40.7795V39.6876V34.6157V33.2112C240.179 32.4262 240.46 31.6683 240.97 31.0795Z'}
            fillRule={'evenodd'}
            stroke={'white'}
            strokeWidth={'1.2'}
          />

          <g clipPath={'url(#clip0_1200_53683)'}>
            <path
              d={'M272.226 30.1631H283.439C283.712 30.1631 283.975 30.262 284.179 30.4401L285.005 31.1651C285.209 31.3433 285.339 31.5893 285.37 31.8557L286.638 40.9138C286.736 41.7523 286.466 42.5917 285.895 43.2218L284.689 44.5508C284.118 45.1809 283.3 45.5413 282.443 45.5413H273.223C272.364 45.5413 271.547 45.1809 270.976 44.5508L269.77 43.2218C269.199 42.5917 268.928 41.7523 269.027 40.9138L270.295 31.8557C270.327 31.5893 270.456 31.3433 270.66 31.1651L271.486 30.4401C271.69 30.2612 271.953 30.1631 272.226 30.1631Z'}
              stroke={'white'}
              strokeMiterlimit={'10'}
              strokeWidth={'1.2'}
            />

            <path
              d={'M281.581 33.1943V27.7437C281.581 27.2951 281.384 26.8684 281.042 26.5726L280.619 26.2064C280.33 25.9555 279.956 25.8174 279.571 25.8174H276.094C275.709 25.8174 275.335 25.9555 275.046 26.2064L274.623 26.5726C274.281 26.8692 274.084 27.2959 274.084 27.7437V33.1943'}
              stroke={'white'}
              strokeMiterlimit={'10'}
              strokeWidth={'1.2'}
            />

            <text
              fill={'#fff'}
              fontFamily={'Saira-Regular, Saira'}
              fontSize={11}
              transform={'translate(275 42)'}
            >
              {'3'}
            </text>
          </g>

          <path
            d={'M22.6222 27.7454C21.5173 26.1454 17.2988 26.1454 16.0935 27.7454C15.7922 28.3454 15.5913 29.2454 15.5913 30.5454C15.5913 31.8454 15.7922 32.7454 16.0935 33.2454C17.1984 34.8454 21.5173 34.9454 22.6222 33.3454C23.0239 32.8454 23.1244 31.8454 23.1244 30.5454C23.2248 29.2454 23.0239 28.3454 22.6222 27.7454Z'}
            stroke={'white'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.2'}
          />

          <path
            d={'M34.4742 27.7454C33.3694 26.1454 29.0504 26.1454 27.9456 27.7454C27.5438 28.2454 27.4434 29.1454 27.4434 30.4454C27.4434 31.7454 27.6442 32.6454 27.9456 33.1454C29.0504 34.7454 33.3694 34.8454 34.4742 33.2454C34.876 32.7454 34.9764 31.7454 34.9764 30.4454C34.9764 29.2454 34.7755 28.3454 34.4742 27.7454Z'}
            stroke={'white'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.2'}
          />

          <path
            d={'M22.6222 39.5452C21.5173 37.9452 17.1984 37.9452 16.0935 39.5452C15.6917 40.0452 15.5913 40.9452 15.5913 42.2452C15.5913 43.5452 15.7922 44.4452 16.0935 44.9452C17.1984 46.5452 21.5173 46.6452 22.6222 45.0452C23.0239 44.5452 23.1244 43.5452 23.1244 42.2452C23.2248 40.9452 23.0239 40.0452 22.6222 39.5452Z'}
            stroke={'white'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.2'}
          />

          <path
            d={'M34.4742 39.5452C33.3694 37.9452 29.0504 37.9452 27.9456 39.5452C27.5438 40.0452 27.4434 40.9452 27.4434 42.2452C27.4434 43.5452 27.6442 44.4452 27.9456 44.9452C29.0504 46.5452 33.3694 46.6452 34.4742 45.0452C34.876 44.5452 34.9764 43.5452 34.9764 42.2452C34.9764 40.9452 34.7755 40.0452 34.4742 39.5452Z'}
            stroke={'white'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.2'}
          />

          <g>
            <g
              clipPath={'url(#prefix__a)'}
              transform={'translate(-83 -48)'}
            >
              <image
                height={160}
                href={logoUrl ?? logoPlaceholder}
                preserveAspectRatio={'xMidYMid slice'}
                transform={'matrix(.448 0 0 .448 200.397 154.797)'}
                width={160}
                xlinkHref={logoUrl ?? logoPlaceholder}
              />
            </g>

            <foreignObject
              height={25}
              width={290}
              x={'8'}
              y={'183'}
            >
              <Description>
                {description}
              </Description>
            </foreignObject>
          </g>

          <path
            d={'M0.817871 62.7058H306.971'}
            opacity={'0.1'}
            stroke={'white'}
            strokeLinecap={'square'}
          />

          <rect
            fill={'white'}
            height={'81.7089'}
            rx={'7.5'}
            stroke={'#F6F7F8'}
            width={'280.219'}
            x={'13.5576'}
            y={'487.197'}
          />

          <path
            d={'M13.0576 494.697C13.0576 490.279 16.6393 486.697 21.0576 486.697H110.34V569.406H21.0576C16.6393 569.406 13.0576 565.824 13.0576 561.406V494.697Z'}
            fill={'#F6F7F8'}
          />

          <rect
            fill={'white'}
            height={'81.7089'}
            rx={'7.5'}
            stroke={'#F6F7F8'}
            width={'280.219'}
            x={'13.582'}
            y={'577.768'}
          />

          <path
            d={'M13.082 585.268C13.082 580.849 16.6638 577.268 21.082 577.268H110.364V659.977H21.082C16.6638 659.977 13.082 656.395 13.082 651.977V585.268Z'}
            fill={'#F6F7F8'}
          />

          {!isEmpty(products) ? (
            <>
              <g clipPath={'url(#prefix__b)'}>
                <image
                  height={134}
                  href={resolveImageUrl(firstProduct.thumbnail) ?? noImage}
                  preserveAspectRatio={'xMidYMid slice'}
                  transform={'translate(13 280)'}
                  width={134}
                  xlinkHref={resolveImageUrl(firstProduct.thumbnail) ?? noImage}
                />
              </g>

              <foreignObject
                height={18}
                width={134}
                x={13}
                y={422}
              >
                <ProductInfo color={'#59677c'}>
                  {firstProduct.name}

                  {firstProduct.location && (
                    <ProductAdditionalInfo>
                      {translate(`products.location.${camelCase(firstProduct.location)}`)}
                    </ProductAdditionalInfo>
                  )}
                </ProductInfo>
              </foreignObject>

              <foreignObject
                height={18}
                width={134}
                x={13}
                y={440}
              >
                <ProductInfo color={'#b6bec7'}>
                  {firstProduct?.displayPrice}

                  {firstProduct?.unitOfAccount && (
                    <ProductAdditionalInfo>
                      {unitOfAccountTypes.includes(firstProduct.unitOfAccount) ? translate('products.per', {
                        // $FlowFixMe
                        unitOfAccount: translate(`products.unitOfAccount.${firstProduct.unitOfAccount}`)
                      }) : translate('products.per', {
                        unitOfAccount: firstProduct.unitOfAccount
                      })}
                    </ProductAdditionalInfo>
                  )}
                </ProductInfo>
              </foreignObject>

              <g clipPath={'url(#prefix__c)'}>
                <image
                  height={134}
                  href={resolveImageUrl(secondProduct.thumbnail) ?? noImage}
                  preserveAspectRatio={'xMidYMid slice'}
                  transform={'translate(159 280)'}
                  width={134}
                  xlinkHref={resolveImageUrl(secondProduct.thumbnail) ?? noImage}
                />
              </g>

              <foreignObject
                height={18}
                width={134}
                x={159}
                y={422}
              >
                <ProductInfo color={'#59677c'}>
                  {secondProduct.name}

                  {secondProduct.location && (
                    <ProductAdditionalInfo>
                      {translate(`products.location.${camelCase(secondProduct.location)}`)}
                    </ProductAdditionalInfo>
                  )}
                </ProductInfo>
              </foreignObject>

              <foreignObject
                height={18}
                width={134}
                x={159}
                y={440}
              >
                <ProductInfo color={'#b6bec7'}>
                  {secondProduct?.displayPrice}

                  {secondProduct?.unitOfAccount && (
                    <ProductAdditionalInfo>
                      {unitOfAccountTypes.includes(secondProduct.unitOfAccount) ? translate('products.per', {
                        // $FlowFixMe
                        unitOfAccount: translate(`products.unitOfAccount.${secondProduct.unitOfAccount}`)
                      }) : translate('products.per', {
                        unitOfAccount: secondProduct.unitOfAccount
                      })}
                    </ProductAdditionalInfo>
                  )}
                </ProductInfo>
              </foreignObject>
            </>
          ) : (
            <g
              fill={'#edeff1'}
              transform={'matrix(.91 0 0 .91 -63 -21)'}
              xmlns={'http://www.w3.org/2000/svg'}
            >
              <defs>
                <path
                  d={'M249.752 484.683h31.808a1 1 0 001-1v-8.965a1 1 0 00-1-1h-31.808a1 1 0 00-1 1v8.965a1 1 0 001 1z'}
                  id={'mobile_app_preview_empty_state__a'}
                />

                <path
                  d={'M289.042 484.683h69.271a1 1 0 001-1v-8.965a1 1 0 00-1-1h-69.271a1 1 0 00-1 1v8.965a1 1 0 001 1z'}
                  id={'mobile_app_preview_empty_state__b'}
                />

                <path
                  d={'M249.752 502.274h49.169a1 1 0 001-1v-8.965a1 1 0 00-1-1h-49.169a1 1 0 00-1 1v8.965a1 1 0 001 1z'}
                  id={'mobile_app_preview_empty_state__c'}
                />

                <path
                  d={'M222 465.021H86a2 2 0 01-2-2V328.993a2 2 0 012-2h136a2 2 0 012 2v134.028a2 2 0 01-2 2z'}
                  id={'mobile_app_preview_empty_state__d'}
                />
              </defs>

              <use
                overflow={'visible'}
                xlinkHref={'#mobile_app_preview_empty_state__a'}
                xmlnsXlink={'http://www.w3.org/1999/xlink'}
              />

              <use
                overflow={'visible'}
                xlinkHref={'#mobile_app_preview_empty_state__b'}
                xmlnsXlink={'http://www.w3.org/1999/xlink'}
              />

              <use
                overflow={'visible'}
                xlinkHref={'#mobile_app_preview_empty_state__c'}
                xmlnsXlink={'http://www.w3.org/1999/xlink'}
              />

              <use
                overflow={'visible'}
                xlinkHref={'#mobile_app_preview_empty_state__d'}
                xmlnsXlink={'http://www.w3.org/1999/xlink'}
              />

              <path d={'M84.858 311h47.208a.858.858 0 00.858-.858v-8.583a.858.858 0 00-.858-.858H84.858a.858.858 0 00-.858.858v8.583c0 .473.384.858.858.858zM138.933 311h44.633a.858.858 0 00.858-.858v-8.583a.858.858 0 00-.858-.858h-44.633a.858.858 0 00-.858.858v8.583c0 .473.384.858.858.858zM328.058 311h44.633a.858.858 0 00.858-.858v-8.583a.858.858 0 00-.858-.858h-44.633a.858.858 0 00-.858.858v8.583c0 .473.384.858.858.858zM379.558 311h8.583a.858.858 0 00.858-.858v-8.583a.858.858 0 00-.858-.858h-8.583a.858.858 0 00-.858.858v8.583c0 .473.384.858.858.858z'} />

              <path d={'M387 465.021H251a2 2 0 01-2-2V328.993a2 2 0 012-2h136a2 2 0 012 2v134.028a2 2 0 01-2 2z'} />

              <path d={'M157.808 484.102h30.287a.865.865 0 00.865-.865v-8.653a.865.865 0 00-.865-.865h-30.287a.865.865 0 00-.865.865v8.653a.865.865 0 00.865.865zM85.12 484.102h65.765a.865.865 0 00.865-.865v-8.653a.865.865 0 00-.865-.865H85.12a.865.865 0 00-.865.865v8.653a.865.865 0 00.865.865zM85.12 502.274h38.94a.865.865 0 00.865-.865v-8.653a.865.865 0 00-.865-.865H85.12a.865.865 0 00-.865.865v8.653a.864.864 0 00.865.865z'} />

              <g transform={'translate(12 42)'}>
                <path d={'M190.057,572.22h81.9c0.486,0,0.881-0.394,0.881-0.881v-8.806c0-0.486-0.394-0.881-0.881-0.881h-81.9c-0.486,0-0.881,0.394-0.881,0.881v8.806C189.177,571.825,189.571,572.22,190.057,572.22z'} />
                <path d={'M190.057,544.92h125.051c0.486,0,0.881-0.394,0.881-0.881v-8.806c0-0.486-0.394-0.881-0.881-0.881H190.057c-0.486,0-0.881,0.394-0.881,0.881v8.806C189.177,544.526,189.571,544.92,190.057,544.92z'} />
                <path d={'M190.057,591.594h36.987c0.486,0,0.881-0.394,0.881-0.881v-8.806c0-0.486-0.394-0.881-0.881-0.881h-36.987c-0.486,0-0.881,0.394-0.881,0.881v8.806C189.177,591.2,189.571,591.594,190.057,591.594z'} />
                <path d={'M279.002,572.22h73.093c0.486,0,0.881-0.394,0.881-0.881v-8.806c0-0.486-0.394-0.881-0.881-0.881h-73.093c-0.486,0-0.881,0.394-0.881,0.881v8.806C278.121,571.825,278.516,572.22,279.002,572.22z'} />
                <path d={'M234.089,591.594h35.226c0.486,0,0.881-0.394,0.881-0.881v-8.806c0-0.486-0.394-0.881-0.881-0.881h-35.226c-0.486,0-0.881,0.394-0.881,0.881v8.806C233.209,591.2,233.603,591.594,234.089,591.594z'} />
                <path d={'M276.36,591.594h35.226c0.486,0,0.881-0.394,0.881-0.881v-8.806c0-0.486-0.394-0.881-0.881-0.881H276.36 c-0.486,0-0.881,0.394-0.881,0.881v8.806C275.48,591.2,275.874,591.594,276.36,591.594z'} />
              </g>

              <g transform={'translate(12 142)'}>
                <path d={'M190.057,544.92h125.051c0.486,0,0.881-0.394,0.881-0.881v-8.806c0-0.486-0.394-0.881-0.881-0.881H190.057c-0.486,0-0.881,0.394-0.881,0.881v8.806C189.177,544.526,189.571,544.92,190.057,544.92z'} />
              </g>
            </g>
          )}

          {!isEmpty(products) && (
            <>
              <path
                clipRule={'evenodd'}
                d={'M49.0227 514.401H74.5899C75.2584 514.401 75.9035 514.629 76.4013 515.041L78.4266 516.716C78.9262 517.128 79.2425 517.696 79.318 518.313L82.4252 539.241C82.666 541.178 82.0028 543.118 80.6029 544.573L77.6521 547.643C76.2522 549.098 74.2502 549.93 72.1494 549.93H51.4632C49.3606 549.93 47.3586 549.098 45.9605 547.643L43.0097 544.573C41.6097 543.118 40.9484 541.178 41.1892 539.241L44.2928 518.313C44.3701 517.696 44.6882 517.128 45.1859 516.716L47.2113 515.041C47.7109 514.629 48.3542 514.401 49.0227 514.401Z'}
                fillRule={'evenodd'}
                stroke={'#3366FF'}
                strokeWidth={'1.5'}
              />

              <path
                d={'M71.546 521.957V516.91V510.69C71.546 509.591 71.0356 508.545 70.1479 507.818L69.0463 506.921C68.2933 506.306 67.3246 505.968 66.3237 505.968H64.9255H60.0231H57.2897C56.2869 505.968 55.3201 506.306 54.5671 506.921L53.4655 507.818C52.576 508.545 52.0674 509.591 52.0674 510.69V512.502V521.957'}
                stroke={'#3366FF'}
                strokeWidth={'1.5'}
              />

              <foreignObject
                height={20}
                width={164}
                x={121}
                y={494}
              >
                <Caption
                  color={color('primary')}
                  size={'medium'}
                >
                  {'New arrivals'}
                </Caption>
              </foreignObject>

              <foreignObject
                height={33}
                width={164}
                x={121}
                y={518}
              >
                <CheckoutLabel>
                  {'Checkout the latest items added to the store.'}
                </CheckoutLabel>
              </foreignObject>

              <foreignObject
                height={20}
                width={164}
                x={121}
                y={588}
              >
                <Caption
                  color={color('primary')}
                  size={'medium'}
                >
                  {'Share and save!'}
                </Caption>
              </foreignObject>

              <path
                d={'M54.9203 602.783C52.6947 604.332 46.7891 602.624 46.0179 600.071C45.7701 599.246 45.9531 597.989 46.5714 596.295C47.1689 594.662 47.8382 593.588 48.5793 593.072C50.5455 591.705 59.075 600.89 61.516 603.591C61.8564 603.971 62.4817 603.971 62.8245 603.591C65.2631 600.89 73.7903 591.705 75.7611 593.072C76.4999 593.588 77.1692 594.662 77.7667 596.295C78.3851 597.989 78.5703 599.246 78.3202 600.071C77.549 602.624 71.6458 604.332 69.4179 602.783'}
                stroke={'#3366FF'}
                strokeWidth={'1.5'}
              />

              <path
                clipRule={'evenodd'}
                d={'M53.471 641.078H76.7618C79.1788 641.078 81.4827 640.115 83.0787 638.434L85.0528 636.357C86.4066 634.932 87.1515 633.104 87.1515 631.208V628.57V621.828V616.667C87.1515 614.772 86.4066 612.94 85.0528 611.518L83.0787 609.44C81.4827 607.757 79.1788 606.794 76.7618 606.794H70.8671H47.5762C45.1592 606.794 42.8576 607.757 41.2594 609.44L39.2852 611.518C37.9337 612.94 37.1865 614.772 37.1865 616.667V619.304V622.531V631.208C37.1865 633.104 37.9337 634.932 39.2852 636.357L41.2594 638.434C42.8576 640.115 45.1592 641.078 47.5762 641.078H53.471Z'}
                fillRule={'evenodd'}
                stroke={'#3366FF'}
                strokeWidth={'1.5'}
              />

              <path
                d={'M62.168 606.794V641.078'}
                stroke={'#3366FF'}
                strokeWidth={'1.5'}
              />

              <path
                d={'M307.348 612H0V568H307.348V612Z'}
                fill={'url(#linear_gradient_1)'}
              />
            </>
          )}

          <g filter={'url(#filter_1)'}>
            <rect
              fill={'#FAFBFB'}
              height={'56'}
              width={'307.348'}
              y={'612'}
            />
          </g>

          <path
            d={'M31.4377 653V650.648H28.8777V653H27.9737V647.496H28.8777V649.808H31.4377V647.496H32.3337V653H31.4377ZM35.771 653.064C35.035 653.064 34.5097 652.989 34.195 652.84C33.8857 652.685 33.6883 652.427 33.603 652.064C33.5177 651.701 33.475 651.096 33.475 650.248C33.475 649.4 33.5177 648.795 33.603 648.432C33.6883 648.069 33.8857 647.813 34.195 647.664C34.5097 647.509 35.035 647.432 35.771 647.432C36.507 647.432 37.0323 647.509 37.347 647.664C37.6617 647.813 37.8617 648.072 37.947 648.44C38.0323 648.803 38.075 649.405 38.075 650.248C38.075 651.091 38.0323 651.696 37.947 652.064C37.8617 652.427 37.6617 652.685 37.347 652.84C37.0323 652.989 36.507 653.064 35.771 653.064ZM35.771 652.248C36.2617 652.248 36.5923 652.216 36.763 652.152C36.9337 652.088 37.043 651.931 37.091 651.68C37.1443 651.429 37.171 650.952 37.171 650.248C37.171 649.544 37.1443 649.067 37.091 648.816C37.043 648.565 36.9337 648.408 36.763 648.344C36.5923 648.28 36.2617 648.248 35.771 648.248C35.2803 648.248 34.9497 648.28 34.779 648.344C34.6137 648.408 34.5043 648.565 34.451 648.816C34.403 649.067 34.379 649.544 34.379 650.248C34.379 650.952 34.403 651.429 34.451 651.68C34.5043 651.931 34.6137 652.088 34.779 652.152C34.9497 652.216 35.2803 652.248 35.771 652.248ZM44.0913 653V648.976H44.0593L42.3953 653H41.7553L40.0993 648.976H40.0673V653H39.2193V647.496H40.4513L42.0753 651.392H42.1153L43.7393 647.496H44.9633V653H44.0913ZM46.2952 653V647.496H49.9672V648.312H47.1992V649.816H49.5912V650.632H47.1992V652.184H49.9672V653H46.2952Z'}
            fill={'#3366FF'}
          />

          <mask
            fill={'white'}
            id={'path-53-inside-1_1200_53683'}
          >
            <path
              clipRule={'evenodd'}
              d={'M38.3631 620.08H38.4009H39.2213H39.259L39.2966 620.084C40.0692 620.162 40.7859 620.519 41.3137 621.086C41.3143 621.087 41.3149 621.087 41.3155 621.088L49.5184 629.801L48.4263 630.829L40.2199 622.113L40.2164 622.109L40.2164 622.109C39.9442 621.816 39.5771 621.629 39.1801 621.58H38.4421C38.045 621.629 37.6779 621.816 37.4057 622.109L37.3986 622.116L37.3986 622.116L29.0746 630.833L27.9897 629.797L36.3104 621.084C36.838 620.518 37.5539 620.162 38.3256 620.084L38.3631 620.08ZM31.7125 630.545V637.407C31.7125 637.944 31.9438 638.455 32.3488 638.811C32.3488 638.811 32.3489 638.811 32.3489 638.811C32.349 638.811 32.3491 638.811 32.3492 638.811L33.0205 639.399L33.0272 639.405L33.0271 639.405L33.0533 639.428C33.3959 639.727 33.8366 639.893 34.2937 639.893H43.2111C43.6686 639.893 44.1106 639.727 44.4537 639.426L44.454 639.425L45.1553 638.811C45.1554 638.811 45.1554 638.811 45.1555 638.811C45.5605 638.455 45.7923 637.944 45.7923 637.407V630.545H47.2923V637.407C47.2923 638.378 46.8732 639.299 46.145 639.938L46.1442 639.939L45.4425 640.554C45.4424 640.554 45.4423 640.554 45.4422 640.554C44.8251 641.095 44.0317 641.393 43.2111 641.393H34.2937C33.4723 641.393 32.6788 641.095 32.0616 640.554L32.0552 640.548L32.0553 640.548L32.029 640.524L31.3606 639.939L31.3602 639.939C30.6306 639.299 30.2125 638.377 30.2125 637.407V630.545H31.7125ZM41.456 633.371C41.1539 632.932 40.6885 632.659 40.2347 632.497ZM40.2347 632.497C39.7719 632.332 39.2534 632.256 38.7516 632.256ZM38.7516 632.256C38.2498 632.256 37.7314 632.332 37.2688 632.497ZM37.2688 632.497C36.8155 632.659 36.3504 632.932 36.0487 633.371ZM36.0487 633.371C35.6771 633.91 35.5665 634.693 35.5665 635.543V638.745H37.0665V635.543C37.0665 634.741 37.1881 634.361 37.2835 634.223L37.2835 634.223L37.2849 634.221C37.3445 634.134 37.4907 634.011 37.7738 633.91C38.048 633.812 38.3916 633.756 38.7518 633.756C39.112 633.756 39.4556 633.812 39.73 633.91C40.0133 634.011 40.1598 634.134 40.2199 634.221L40.2204 634.222C40.3158 634.36 40.4372 634.741 40.4372 635.543V638.745H41.9372V635.543C41.9372 634.693 41.8265 633.91 41.4562 633.372'}
              fillRule={'evenodd'}
            />
          </mask>

          <path
            d={'M40.2347 632.497L40.6712 631.887L40.5284 631.785L40.3551 631.757L40.2347 632.497ZM38.7516 632.256L38.872 631.516L38.7515 631.496L38.6311 631.516L38.7516 632.256ZM37.2688 632.497L37.1482 631.757L36.975 631.785L36.8322 631.888L37.2688 632.497ZM36.0487 633.371L35.6121 632.761L33.7131 634.121L36.0486 634.121L36.0487 633.371ZM41.8925 632.761L40.6712 631.887L39.7982 633.107L41.0195 633.981L41.8925 632.761ZM40.3551 631.757L38.872 631.516L38.6312 632.996L40.1143 633.237L40.3551 631.757ZM38.6311 631.516L37.1482 631.757L37.3893 633.238L38.8722 632.996L38.6311 631.516ZM36.8322 631.888L35.6121 632.761L36.4853 633.981L37.7054 633.107L36.8322 631.888ZM41.4563 632.622L36.0488 632.621L36.0486 634.121L41.4561 634.122L41.4563 632.622ZM38.3631 620.08V618.58H38.2876L38.2125 618.588L38.3631 620.08ZM39.259 620.08L39.4096 618.588L39.3345 618.58H39.259V620.08ZM39.2966 620.084L39.146 621.577L39.146 621.577L39.2966 620.084ZM41.3137 621.086L40.2154 622.108L40.2156 622.108L41.3137 621.086ZM41.3155 621.088L40.2161 622.109L40.2233 622.116L41.3155 621.088ZM49.5184 629.801L50.5466 630.893L51.6388 629.865L50.6106 628.773L49.5184 629.801ZM48.4263 630.829L47.3341 631.857L48.3624 632.95L49.4545 631.921L48.4263 630.829ZM40.2199 622.113L39.1277 623.141L39.1277 623.141L40.2199 622.113ZM40.2164 622.109L39.1877 621.017L38.0965 622.045L39.1242 623.137L40.2164 622.109ZM40.2164 622.109L41.2451 623.2L42.3289 622.179L41.3155 621.088L40.2164 622.109ZM39.1801 621.58L39.3608 620.091L39.2708 620.08H39.1801V621.58ZM38.4421 621.58V620.08H38.3513L38.2613 620.091L38.4421 621.58ZM37.4057 622.109L36.3066 621.088L36.3066 621.088L37.4057 622.109ZM37.3986 622.116L36.3642 623.203L37.4642 624.25L38.4978 623.137L37.3986 622.116ZM37.3986 622.116L38.433 621.03L37.3483 619.997L36.3138 621.08L37.3986 622.116ZM29.0746 630.833L28.0386 631.918L29.1234 632.954L30.1594 631.869L29.0746 630.833ZM27.9897 629.797L26.9049 628.761L25.869 629.846L26.9538 630.882L27.9897 629.797ZM36.3104 621.084L37.3952 622.12L37.4015 622.113L37.4078 622.107L36.3104 621.084ZM38.3256 620.084L38.4761 621.577L38.4761 621.577L38.3256 620.084ZM31.7125 630.545H33.2125V629.045H31.7125V630.545ZM32.3488 638.811L33.342 637.687L33.338 637.683L32.3488 638.811ZM32.3489 638.811L31.3557 639.935L31.3593 639.938L32.3489 638.811ZM32.3492 638.811L31.3595 639.938L31.3609 639.939L32.3492 638.811ZM33.0205 639.399L34.0087 638.27L34.0087 638.27L33.0205 639.399ZM33.0272 639.405L34.1475 640.402L35.1531 639.273L34.0154 638.276L33.0272 639.405ZM33.0271 639.405L31.9068 638.407L30.913 639.524L32.0257 640.522L33.0271 639.405ZM33.0533 639.428L32.0518 640.545L32.0593 640.552L32.0668 640.558L33.0533 639.428ZM44.4537 639.426L43.4669 638.296L43.4648 638.298L44.4537 639.426ZM44.454 639.425L45.4408 640.555L45.4422 640.554L44.454 639.425ZM45.1553 638.811L44.1696 637.681L44.1671 637.683L45.1553 638.811ZM45.1555 638.811L46.1412 639.942L46.1454 639.938L45.1555 638.811ZM45.7923 630.545V629.045H44.2923V630.545H45.7923ZM47.2923 630.545H48.7923V629.045H47.2923V630.545ZM46.145 639.938L47.1345 641.066L47.1347 641.066L46.145 639.938ZM46.1442 639.939L47.1324 641.068L47.1337 641.067L46.1442 639.939ZM45.4425 640.554L44.4543 639.425L44.4504 639.429L45.4425 640.554ZM45.4422 640.554L46.4309 641.682L46.4343 641.679L45.4422 640.554ZM32.0616 640.554L31.0727 641.682L31.0727 641.682L32.0616 640.554ZM32.0552 640.548L30.9395 639.545L29.9245 640.675L31.0664 641.676L32.0552 640.548ZM32.0553 640.548L33.1709 641.551L34.1746 640.434L33.0567 639.431L32.0553 640.548ZM32.029 640.524L33.0304 639.408L33.0238 639.402L33.0172 639.396L32.029 640.524ZM31.3606 639.939L30.3718 641.067L30.3723 641.068L31.3606 639.939ZM31.3602 639.939L30.3712 641.067L30.3714 641.067L31.3602 639.939ZM30.2125 630.545V629.045H28.7125V630.545H30.2125ZM40.2347 632.497L39.73 633.91L40.2347 632.497ZM38.7516 632.256L38.7515 630.756L38.7516 632.256ZM37.2688 632.497L37.7739 633.91L37.2688 632.497ZM36.0487 633.371L37.2834 634.223L37.285 634.22L36.0487 633.371ZM35.5665 638.745H34.0665V640.245H35.5665V638.745ZM37.0665 638.745V640.245H38.5665V638.745H37.0665ZM37.2835 634.223L38.1155 632.974L36.888 632.156L36.0494 633.37L37.2835 634.223ZM37.2835 634.223L36.4514 635.471L37.6829 636.292L38.5203 635.071L37.2835 634.223ZM37.2849 634.221L38.5217 635.069L38.5217 635.069L37.2849 634.221ZM37.7739 633.91L37.2688 632.497L37.7739 633.91ZM38.7518 633.756L38.7519 635.256L38.7518 633.756ZM39.73 633.91L39.2252 635.322L39.2252 635.322L39.73 633.91ZM40.2199 634.221L38.9836 635.07L38.9841 635.071L40.2199 634.221ZM40.2204 634.222L38.9846 635.072L38.985 635.072L40.2204 634.222ZM40.4372 638.745H38.9372V640.245H40.4372V638.745ZM41.9372 638.745V640.245H43.4372V638.745H41.9372ZM38.4009 618.58H38.3631V621.58H38.4009V618.58ZM39.2213 618.58H38.4009V621.58H39.2213V618.58ZM39.259 618.58H39.2213V621.58H39.259V618.58ZM39.4471 618.592L39.4096 618.588L39.1084 621.573L39.146 621.577L39.4471 618.592ZM42.4119 620.064C41.6363 619.231 40.5829 618.706 39.4471 618.592L39.146 621.577C39.5556 621.618 39.9356 621.807 40.2154 622.108L42.4119 620.064ZM42.4148 620.067C42.4136 620.066 42.4125 620.065 42.4117 620.064L40.2156 622.108L40.2162 622.109L42.4148 620.067ZM50.6106 628.773L42.4076 620.06L40.2233 622.116L48.4263 630.829L50.6106 628.773ZM49.4545 631.921L50.5466 630.893L48.4902 628.709L47.3981 629.737L49.4545 631.921ZM39.1277 623.141L47.3341 631.857L49.5184 629.801L41.312 621.084L39.1277 623.141ZM39.1242 623.137L39.1277 623.141L41.312 621.084L41.3085 621.081L39.1242 623.137ZM39.1877 621.017L39.1877 621.017L41.2451 623.201L41.2451 623.2L39.1877 621.017ZM38.9993 623.069C39.0444 623.075 39.0862 623.096 39.1173 623.13L41.3155 621.088C40.8022 620.535 40.1098 620.182 39.3608 620.091L38.9993 623.069ZM38.4421 623.08H39.1801V620.08H38.4421V623.08ZM38.5048 623.13C38.5359 623.096 38.5777 623.075 38.6228 623.069L38.2613 620.091C37.5123 620.182 36.8199 620.535 36.3066 621.088L38.5048 623.13ZM38.4978 623.137L38.5049 623.13L36.3066 621.088L36.2995 621.096L38.4978 623.137ZM36.3642 623.203L36.3642 623.203L38.4331 621.03L38.433 621.03L36.3642 623.203ZM30.1594 631.869L38.4834 623.152L36.3138 621.08L27.9897 629.797L30.1594 631.869ZM26.9538 630.882L28.0386 631.918L30.1105 629.748L29.0257 628.712L26.9538 630.882ZM35.2256 620.048L26.9049 628.761L29.0746 630.833L37.3952 622.12L35.2256 620.048ZM38.175 618.592C37.0407 618.706 35.9885 619.229 35.2131 620.061L37.4078 622.107C37.6875 621.806 38.067 621.618 38.4761 621.577L38.175 618.592ZM38.2125 618.588L38.175 618.592L38.4761 621.577L38.5137 621.573L38.2125 618.588ZM33.2125 637.407V630.545H30.2125V637.407H33.2125ZM33.338 637.683C33.2573 637.612 33.2125 637.512 33.2125 637.407H30.2125C30.2125 638.377 30.6303 639.298 31.3595 639.938L33.338 637.683ZM33.3421 637.687C33.3394 637.684 33.3371 637.682 33.3358 637.681C33.3351 637.681 33.3345 637.68 33.3341 637.68C33.334 637.68 33.3338 637.679 33.3337 637.679C33.3337 637.679 33.3337 637.679 33.3336 637.679C33.3336 637.679 33.3336 637.679 33.3336 637.679C33.3336 637.679 33.3336 637.679 33.3336 637.679C33.3336 637.679 33.3336 637.679 33.3336 637.679C33.3334 637.679 33.3345 637.68 33.3357 637.681C33.337 637.682 33.3393 637.684 33.342 637.687L31.3556 639.935C31.3583 639.937 31.3605 639.939 31.3619 639.94C31.3626 639.941 31.3632 639.941 31.3635 639.942C31.3637 639.942 31.3638 639.942 31.3639 639.942C31.364 639.942 31.364 639.942 31.364 639.942C31.364 639.942 31.364 639.942 31.3641 639.942C31.3641 639.942 31.3641 639.942 31.3641 639.942C31.3641 639.942 31.3641 639.942 31.3641 639.942C31.3642 639.942 31.3632 639.941 31.362 639.94C31.3606 639.939 31.3584 639.937 31.3557 639.935L33.3421 637.687ZM33.3388 637.684L33.3385 637.684L31.3593 639.938L31.3595 639.938L33.3388 637.684ZM34.0087 638.27L33.3374 637.683L31.3609 639.939L32.0323 640.527L34.0087 638.27ZM34.0154 638.276L34.0087 638.27L32.0323 640.527L32.0389 640.533L34.0154 638.276ZM34.1475 640.402L34.1475 640.402L31.9068 638.407L31.9068 638.407L34.1475 640.402ZM34.0547 638.312L34.0286 638.288L32.0257 640.522L32.0518 640.545L34.0547 638.312ZM34.2937 638.393C34.1986 638.393 34.1086 638.358 34.0398 638.298L32.0668 640.558C32.6832 641.096 33.4746 641.393 34.2937 641.393V638.393ZM43.2111 638.393H34.2937V641.393H43.2111V638.393ZM43.4648 638.298C43.3957 638.358 43.3054 638.393 43.2111 638.393V641.393C44.0319 641.393 44.8254 641.095 45.4425 640.554L43.4648 638.298ZM43.4672 638.296L43.4669 638.296L45.4405 640.555L45.4408 640.555L43.4672 638.296ZM44.1671 637.683L43.4658 638.297L45.4422 640.554L46.1435 639.94L44.1671 637.683ZM44.1698 637.68C44.1673 637.683 44.1652 637.684 44.164 637.686C44.1633 637.686 44.1628 637.687 44.1625 637.687C44.1624 637.687 44.1622 637.687 44.1621 637.687C44.1621 637.687 44.1621 637.687 44.162 637.687C44.162 637.687 44.162 637.687 44.162 637.687C44.162 637.687 44.162 637.687 44.162 637.687C44.1618 637.687 44.1628 637.687 44.1639 637.686C44.1651 637.685 44.1671 637.683 44.1696 637.681L46.141 639.942C46.1435 639.94 46.1456 639.938 46.1468 639.937C46.1474 639.936 46.148 639.936 46.1483 639.936C46.1484 639.935 46.1486 639.935 46.1487 639.935C46.1487 639.935 46.1487 639.935 46.1488 639.935C46.1488 639.935 46.1488 639.935 46.1488 639.935C46.1488 639.935 46.1488 639.935 46.1488 639.935C46.149 639.935 46.148 639.936 46.1469 639.937C46.1457 639.938 46.1437 639.94 46.1412 639.942L44.1698 637.68ZM44.2923 637.407C44.2923 637.511 44.2477 637.612 44.1656 637.684L46.1454 639.938C46.8734 639.299 47.2923 638.377 47.2923 637.407H44.2923ZM44.2923 630.545V637.407H47.2923V630.545H44.2923ZM47.2923 629.045H45.7923V632.045H47.2923V629.045ZM48.7923 637.407V630.545H45.7923V637.407H48.7923ZM47.1347 641.066C48.1859 640.143 48.7923 638.811 48.7923 637.407H45.7923C45.7923 637.944 45.5605 638.456 45.1553 638.811L47.1347 641.066ZM47.1337 641.067L47.1345 641.066L45.1555 638.811L45.1548 638.812L47.1337 641.067ZM46.4307 641.682L47.1324 641.068L45.156 638.811L44.4543 639.425L46.4307 641.682ZM46.4343 641.679L46.4346 641.679L44.4504 639.429L44.4501 639.429L46.4343 641.679ZM43.2111 642.893C44.3949 642.893 45.5398 642.463 46.4309 641.682L44.4535 639.426C44.1104 639.727 43.6685 639.893 43.2111 639.893V642.893ZM34.2937 642.893H43.2111V639.893H34.2937V642.893ZM31.0727 641.682C31.9641 642.463 33.1093 642.893 34.2937 642.893V639.893C33.8353 639.893 33.3934 639.726 33.0504 639.426L31.0727 641.682ZM31.0664 641.676L31.0727 641.682L33.0504 639.426L33.0441 639.42L31.0664 641.676ZM30.9396 639.545L30.9395 639.545L33.1709 641.551L33.1709 641.551L30.9396 639.545ZM31.0275 641.641L31.0538 641.665L33.0567 639.431L33.0304 639.408L31.0275 641.641ZM30.3723 641.068L31.0408 641.653L33.0172 639.396L32.3488 638.811L30.3723 641.068ZM30.3714 641.067L30.3718 641.067L32.3493 638.811L32.3489 638.811L30.3714 641.067ZM28.7125 637.407C28.7125 638.81 29.3172 640.142 30.3712 641.067L32.3492 638.811C31.9439 638.456 31.7125 637.945 31.7125 637.407H28.7125ZM28.7125 630.545V637.407H31.7125V630.545H28.7125ZM31.7125 629.045H30.2125V632.045H31.7125V629.045ZM42.6921 632.521C42.1478 631.73 41.363 631.307 40.7395 631.085L39.73 633.91C40.014 634.011 40.1601 634.134 40.2199 634.221L42.6921 632.521ZM40.7395 631.085C40.0882 630.852 39.3948 630.756 38.7515 630.756L38.7518 633.756C39.112 633.756 39.4557 633.812 39.73 633.91L40.7395 631.085ZM38.7515 630.756C38.1081 630.756 37.4148 630.852 36.7637 631.085L37.7739 633.91C38.048 633.812 38.3915 633.756 38.7518 633.756L38.7515 630.756ZM36.7637 631.085C36.1409 631.308 35.3564 631.73 34.8124 632.521L37.285 634.22C37.3444 634.134 37.4901 634.011 37.7739 633.91L36.7637 631.085ZM34.814 632.519C34.1668 633.457 34.0665 634.643 34.0665 635.543H37.0665C37.0665 634.744 37.1874 634.362 37.2834 634.223L34.814 632.519ZM34.0665 635.543V638.745H37.0665V635.543H34.0665ZM35.5665 640.245H37.0665V637.245H35.5665V640.245ZM38.5665 638.745V635.543H35.5665V638.745H38.5665ZM38.5665 635.543C38.5665 635.213 38.5919 635.019 38.6104 634.927C38.6194 634.881 38.6244 634.872 38.6175 634.891C38.6129 634.904 38.5858 634.976 38.5176 635.075L36.0494 633.37C35.8353 633.68 35.7286 634.037 35.6681 634.341C35.6019 634.674 35.5665 635.071 35.5665 635.543H38.5665ZM36.4514 635.471L36.4514 635.471L38.1155 632.974L38.1155 632.974L36.4514 635.471ZM38.5203 635.071L38.5217 635.069L36.048 633.372L36.0467 633.374L38.5203 635.071ZM38.5217 635.069C38.4223 635.214 38.3256 635.286 38.2908 635.31C38.2494 635.338 38.239 635.337 38.2789 635.322L37.2688 632.497C36.813 632.66 36.3488 632.934 36.048 633.372L38.5217 635.069ZM38.2789 635.322C38.3647 635.292 38.5334 635.256 38.7519 635.256L38.7516 632.256C38.2497 632.256 37.7314 632.332 37.2688 632.497L38.2789 635.322ZM38.7519 635.256C38.9705 635.256 39.1393 635.291 39.2252 635.322L40.2347 632.497C39.772 632.332 39.2535 632.256 38.7516 632.256L38.7519 635.256ZM39.2252 635.322C39.2653 635.336 39.255 635.338 39.2138 635.31C39.1791 635.286 39.0827 635.215 38.9836 635.07L41.4562 633.372C41.1551 632.933 40.6907 632.66 40.2347 632.497L39.2252 635.322ZM38.9841 635.071L38.9846 635.072L41.4562 633.371L41.4557 633.371L38.9841 635.071ZM38.985 635.072C38.9173 634.974 38.8905 634.902 38.8859 634.889C38.8793 634.871 38.8843 634.88 38.8934 634.926C38.9118 635.019 38.9372 635.213 38.9372 635.543H41.9372C41.9372 635.071 41.9019 634.674 41.8358 634.341C41.7754 634.037 41.669 633.68 41.4557 633.371L38.985 635.072ZM38.9372 635.543V638.745H41.9372V635.543H38.9372ZM40.4372 640.245H41.9372V637.245H40.4372V640.245ZM43.4372 638.745V635.543H40.4372V638.745H43.4372ZM43.4372 635.543C43.4372 634.642 43.3367 633.458 42.692 632.521L40.2205 634.222C40.3164 634.361 40.4372 634.744 40.4372 635.543H43.4372Z'}
            fill={'#3366FF'}
            mask={'url(#path-53-inside-1_1200_53683)'}
          />

          <rect
            fill={'#3366FF'}
            height={'1.63333'}
            width={'32.7838'}
            x={'21.9243'}
            y={'612'}
          />

          <path
            d={'M107.287 653.064C106.983 653.064 106.647 653.053 106.279 653.032C105.911 653.011 105.589 652.981 105.311 652.944V652.152C105.967 652.216 106.538 652.248 107.023 652.248C107.311 652.248 107.535 652.237 107.695 652.216C107.855 652.189 107.981 652.147 108.071 652.088C108.157 652.035 108.213 651.96 108.239 651.864C108.266 651.763 108.279 651.621 108.279 651.44C108.279 651.243 108.266 651.099 108.239 651.008C108.218 650.917 108.167 650.851 108.087 650.808C108.013 650.765 107.885 650.736 107.703 650.72L106.391 650.568C106.055 650.531 105.797 650.451 105.615 650.328C105.439 650.2 105.317 650.032 105.247 649.824C105.183 649.616 105.151 649.352 105.151 649.032C105.151 648.579 105.231 648.237 105.391 648.008C105.557 647.773 105.781 647.619 106.063 647.544C106.346 647.469 106.714 647.432 107.167 647.432C107.477 647.432 107.794 647.448 108.119 647.48C108.445 647.507 108.709 647.541 108.911 647.584V648.352C108.714 648.32 108.469 648.296 108.175 648.28C107.882 648.259 107.605 648.248 107.343 648.248C107.061 648.248 106.837 648.259 106.671 648.28C106.506 648.301 106.378 648.339 106.287 648.392C106.191 648.445 106.13 648.517 106.103 648.608C106.077 648.693 106.063 648.821 106.063 648.992C106.063 649.184 106.077 649.323 106.103 649.408C106.13 649.488 106.183 649.547 106.263 649.584C106.349 649.621 106.485 649.651 106.671 649.672L107.991 649.824C108.269 649.851 108.49 649.907 108.655 649.992C108.826 650.072 108.954 650.213 109.039 650.416C109.135 650.629 109.183 650.955 109.183 651.392C109.183 651.877 109.106 652.243 108.951 652.488C108.797 652.728 108.589 652.885 108.327 652.96C108.066 653.029 107.719 653.064 107.287 653.064ZM113.728 653V650.648H111.168V653H110.264V647.496H111.168V649.808H113.728V647.496H114.624V653H113.728ZM118.062 653.064C117.326 653.064 116.8 652.989 116.486 652.84C116.176 652.685 115.979 652.427 115.894 652.064C115.808 651.701 115.766 651.096 115.766 650.248C115.766 649.4 115.808 648.795 115.894 648.432C115.979 648.069 116.176 647.813 116.486 647.664C116.8 647.509 117.326 647.432 118.062 647.432C118.798 647.432 119.323 647.509 119.638 647.664C119.952 647.813 120.152 648.072 120.238 648.44C120.323 648.803 120.366 649.405 120.366 650.248C120.366 651.091 120.323 651.696 120.238 652.064C120.152 652.427 119.952 652.685 119.638 652.84C119.323 652.989 118.798 653.064 118.062 653.064ZM118.062 652.248C118.552 652.248 118.883 652.216 119.054 652.152C119.224 652.088 119.334 651.931 119.382 651.68C119.435 651.429 119.462 650.952 119.462 650.248C119.462 649.544 119.435 649.067 119.382 648.816C119.334 648.565 119.224 648.408 119.054 648.344C118.883 648.28 118.552 648.248 118.062 648.248C117.571 648.248 117.24 648.28 117.07 648.344C116.904 648.408 116.795 648.565 116.742 648.816C116.694 649.067 116.67 649.544 116.67 650.248C116.67 650.952 116.694 651.429 116.742 651.68C116.795 651.931 116.904 652.088 117.07 652.152C117.24 652.216 117.571 652.248 118.062 652.248ZM121.51 647.496H123.982C124.425 647.496 124.763 647.568 124.998 647.712C125.238 647.856 125.398 648.056 125.478 648.312C125.563 648.563 125.606 648.888 125.606 649.288C125.606 649.672 125.563 649.992 125.478 650.248C125.398 650.504 125.238 650.707 124.998 650.856C124.758 651.005 124.411 651.08 123.958 651.08H122.414V653H121.51V647.496ZM123.614 650.272C123.955 650.272 124.201 650.245 124.35 650.192C124.499 650.133 124.595 650.04 124.638 649.912C124.686 649.779 124.71 649.571 124.71 649.288C124.71 649.005 124.689 648.8 124.646 648.672C124.603 648.544 124.507 648.453 124.358 648.4C124.214 648.341 123.974 648.312 123.638 648.312H122.414V650.272H123.614Z'}
            fill={'#59677C'}
          />

          <path
            d={'M114.238 634.892C113.49 633.814 110.661 633.814 109.912 634.892C109.663 635.223 109.58 635.803 109.58 636.715C109.58 637.544 109.663 638.124 109.912 638.539C110.661 639.617 113.49 639.617 114.238 638.622C114.488 638.29 114.571 637.627 114.571 636.798C114.571 635.803 114.488 635.223 114.238 634.892ZM124.055 634.892C123.306 633.814 120.477 633.814 119.729 634.892C119.479 635.223 119.396 635.803 119.396 636.715C119.396 637.544 119.479 638.124 119.729 638.539C120.477 639.617 123.306 639.617 124.055 638.622C124.304 638.29 124.387 637.627 124.387 636.798C124.387 635.803 124.304 635.223 124.055 634.892ZM105.919 624.53L109.746 625.11C109.829 625.11 109.912 625.193 109.912 625.276C109.912 626.188 110.079 630.996 110.411 631.161L111.327 631.99C111.576 632.156 111.909 632.322 112.242 632.322H121.975C122.308 632.322 122.64 632.239 122.89 631.99L123.805 631.161C124.055 630.913 124.554 627.017 124.72 625.442C124.72 625.359 124.72 625.193 124.637 625.11L124.387 624.944C124.304 624.862 124.221 624.779 124.138 624.779L106.252 622.209C106.169 622.209 106.169 622.209 106.086 622.292L105.919 622.375C105.836 622.458 105.836 622.458 105.836 622.541L105.586 624.116C105.586 624.198 105.586 624.198 105.67 624.281L105.836 624.447C105.836 624.53 105.919 624.53 105.919 624.53Z'}
            stroke={'#667281'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.5'}
          />

          <path
            d={'M180.978 653V647.496H184.65V648.312H181.882V649.816H184.274V650.632H181.882V652.184H184.65V653H180.978ZM189.334 653L188.846 651.672H186.518L186.046 653H185.134L187.11 647.496H188.286L190.262 653H189.334ZM187.702 648.408H187.662L186.782 650.864H188.582L187.702 648.408ZM194.324 653L193.508 650.92H193.492H191.932V653H191.028V647.496H193.596C194.044 647.496 194.383 647.565 194.612 647.704C194.847 647.837 195.001 648.027 195.076 648.272C195.156 648.512 195.196 648.824 195.196 649.208C195.196 649.576 195.14 649.907 195.028 650.2C194.921 650.488 194.705 650.688 194.38 650.8L195.284 653H194.324ZM193.22 650.112C193.545 650.112 193.783 650.085 193.932 650.032C194.087 649.973 194.185 649.883 194.228 649.76C194.271 649.637 194.292 649.453 194.292 649.208C194.292 648.952 194.271 648.765 194.228 648.648C194.191 648.531 194.1 648.445 193.956 648.392C193.812 648.339 193.577 648.312 193.252 648.312H191.932V650.112H193.22ZM199.818 653L197.218 649.144H197.186V653H196.322V647.496H197.258L199.786 651.216H199.818V647.496H200.682V653H199.818Z'}
            fill={'#59677C'}
          />

          <path
            d={'M201.262 623.825C198.043 619.142 185.934 619.145 182.719 623.825C181.684 625.33 181.167 627.918 181.167 631.588C181.167 635.258 181.684 637.846 182.719 639.351C185.83 643.878 198.068 644.181 201.262 639.536C202.296 638.03 202.813 635.381 202.814 631.588C202.814 627.918 202.297 625.33 201.262 623.825Z'}
            stroke={'white'}
            strokeMiterlimit={'10'}
          />

          <path
            d={'M188.553 624.392C188.013 624.617 187.409 624.64 186.853 624.459C186.297 624.277 185.825 623.902 185.524 623.402C185.457 622.934 185.522 622.457 185.711 622.024C185.824 621.576 186.061 621.168 186.395 620.847C187.065 620.347 189.967 623.701 190.797 624.687C190.824 624.72 190.858 624.746 190.897 624.764C190.935 624.782 190.977 624.791 191.02 624.791C191.062 624.791 191.104 624.782 191.143 624.764C191.181 624.746 191.215 624.72 191.242 624.687C192.073 623.701 194.975 620.347 195.645 620.847C195.979 621.168 196.216 621.576 196.328 622.024C196.518 622.457 196.583 622.934 196.516 623.402C196.214 623.902 195.742 624.277 195.186 624.459C194.63 624.64 194.026 624.617 193.486 624.392'}
            stroke={'#667281'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.5'}
          />

          <path
            d={'M188.047 639.332H196.006C196.415 639.332 196.819 639.246 197.191 639.078C197.563 638.911 197.895 638.666 198.165 638.361L198.84 637.597C199.302 637.074 199.557 636.402 199.557 635.705V630.921C199.557 630.224 199.302 629.552 198.84 629.029L198.165 628.265C197.895 627.96 197.563 627.716 197.191 627.548C196.819 627.381 196.415 627.294 196.006 627.294H186.033C185.625 627.294 185.221 627.381 184.849 627.548C184.477 627.716 184.145 627.96 183.875 628.265L183.2 629.029C182.737 629.552 182.482 630.224 182.482 630.921V635.705C182.482 636.402 182.737 637.074 183.2 637.597L183.875 638.361C184.144 638.666 184.477 638.911 184.849 639.078C185.221 639.246 185.625 639.332 186.033 639.332L188.047 639.332Z'}
            stroke={'#667281'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.5'}
          />

          <path
            d={'M191.021 627.294V632.389'}
            stroke={'#667281'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.5'}
          />

          <path d={'M191.021 632.389V639.332'}
            stroke={'#667281'}
            strokeMiterlimit={'10'}
            strokeWidth={'1.5'}
          />

          <path
            d={'M254.891 653L254.403 651.672H252.075L251.603 653H250.691L252.667 647.496H253.843L255.819 653H254.891ZM253.259 648.408H253.219L252.339 650.864H254.139L253.259 648.408ZM260.016 652.944C259.84 652.981 259.624 653.011 259.368 653.032C259.118 653.053 258.883 653.064 258.664 653.064C257.998 653.064 257.507 652.987 257.192 652.832C256.878 652.677 256.667 652.408 256.56 652.024C256.454 651.64 256.4 651.048 256.4 650.248C256.4 649.448 256.454 648.856 256.56 648.472C256.667 648.088 256.878 647.819 257.192 647.664C257.507 647.509 257.998 647.432 258.664 647.432C258.888 647.432 259.123 647.445 259.368 647.472C259.619 647.499 259.824 647.531 259.984 647.568V648.328C259.558 648.275 259.192 648.248 258.888 648.248C258.366 648.248 258.003 648.288 257.8 648.368C257.598 648.448 257.464 648.616 257.4 648.872C257.336 649.128 257.304 649.587 257.304 650.248C257.304 650.909 257.336 651.368 257.4 651.624C257.464 651.88 257.598 652.048 257.8 652.128C258.003 652.208 258.366 652.248 258.888 652.248C259.304 652.248 259.68 652.221 260.016 652.168V652.944ZM264.467 652.944C264.291 652.981 264.075 653.011 263.819 653.032C263.568 653.053 263.334 653.064 263.115 653.064C262.448 653.064 261.958 652.987 261.643 652.832C261.328 652.677 261.118 652.408 261.011 652.024C260.904 651.64 260.851 651.048 260.851 650.248C260.851 649.448 260.904 648.856 261.011 648.472C261.118 648.088 261.328 647.819 261.643 647.664C261.958 647.509 262.448 647.432 263.115 647.432C263.339 647.432 263.574 647.445 263.819 647.472C264.07 647.499 264.275 647.531 264.435 647.568V648.328C264.008 648.275 263.643 648.248 263.339 648.248C262.816 648.248 262.454 648.288 262.251 648.368C262.048 648.448 261.915 648.616 261.851 648.872C261.787 649.128 261.755 649.587 261.755 650.248C261.755 650.909 261.787 651.368 261.851 651.624C261.915 651.88 262.048 652.048 262.251 652.128C262.454 652.208 262.816 652.248 263.339 652.248C263.755 652.248 264.131 652.221 264.467 652.168V652.944ZM267.598 653.064C266.862 653.064 266.337 652.989 266.022 652.84C265.713 652.685 265.515 652.427 265.43 652.064C265.345 651.701 265.302 651.096 265.302 650.248C265.302 649.4 265.345 648.795 265.43 648.432C265.515 648.069 265.713 647.813 266.022 647.664C266.337 647.509 266.862 647.432 267.598 647.432C268.334 647.432 268.859 647.509 269.174 647.664C269.489 647.813 269.689 648.072 269.774 648.44C269.859 648.803 269.902 649.405 269.902 650.248C269.902 651.091 269.859 651.696 269.774 652.064C269.689 652.427 269.489 652.685 269.174 652.84C268.859 652.989 268.334 653.064 267.598 653.064ZM267.598 652.248C268.089 652.248 268.419 652.216 268.59 652.152C268.761 652.088 268.87 651.931 268.918 651.68C268.971 651.429 268.998 650.952 268.998 650.248C268.998 649.544 268.971 649.067 268.918 648.816C268.87 648.565 268.761 648.408 268.59 648.344C268.419 648.28 268.089 648.248 267.598 648.248C267.107 648.248 266.777 648.28 266.606 648.344C266.441 648.408 266.331 648.565 266.278 648.816C266.23 649.067 266.206 649.544 266.206 650.248C266.206 650.952 266.23 651.429 266.278 651.68C266.331 651.931 266.441 652.088 266.606 652.152C266.777 652.216 267.107 652.248 267.598 652.248ZM275.27 647.496V650.56C275.27 651.291 275.222 651.824 275.126 652.16C275.03 652.496 274.836 652.731 274.542 652.864C274.249 652.997 273.78 653.064 273.134 653.064C272.489 653.064 272.017 652.997 271.718 652.864C271.425 652.731 271.228 652.496 271.126 652.16C271.03 651.819 270.982 651.285 270.982 650.56V647.496H271.886V650.84C271.886 651.288 271.908 651.603 271.95 651.784C271.998 651.96 272.105 652.083 272.27 652.152C272.436 652.216 272.724 652.248 273.134 652.248C273.54 652.248 273.825 652.216 273.99 652.152C274.156 652.083 274.26 651.96 274.302 651.784C274.345 651.603 274.366 651.288 274.366 650.84V647.496H275.27ZM280.04 653L277.44 649.144H277.408V653H276.544V647.496H277.48L280.008 651.216H280.04V647.496H280.904V653H280.04ZM283.365 653V648.336H281.693V647.496H285.941V648.336H284.269V653H283.365Z'}
            fill={'#59677C'}
          />

          <mask
            fill={'white'}
            id={'path-65-inside-2_1200_53683'}
          >
            <path
              clipRule={'evenodd'}
              d={'M273.349 621.892C272.888 621.221 272.153 620.775 271.375 620.496C270.586 620.215 269.687 620.08 268.805 620.08C267.922 620.08 267.023 620.215 266.235 620.496C265.456 620.775 264.72 621.222 264.258 621.895M273.349 621.892C273.936 622.731 274.147 624.032 274.139 625.613C274.139 627.241 273.927 628.568 273.351 629.406L273.351 629.407C272.887 630.081 272.142 630.516 271.359 630.78C270.566 631.046 269.663 631.163 268.78 631.148C267.896 631.134 266.998 630.989 266.215 630.705C265.443 630.426 264.711 629.985 264.259 629.328L264.256 629.324L264.256 629.324C263.685 628.482 263.471 627.197 263.471 625.611C263.471 624.029 263.684 622.731 264.258 621.895M266.74 621.909C266.131 622.126 265.715 622.424 265.495 622.744L265.494 622.744C265.195 623.18 264.971 624.076 264.971 625.611C264.971 627.14 265.193 628.032 265.496 628.48C265.704 628.78 266.113 629.073 266.726 629.295C267.328 629.513 268.058 629.637 268.804 629.649C269.55 629.661 270.279 629.56 270.88 629.358C271.491 629.152 271.902 628.866 272.115 628.556C272.413 628.123 272.639 627.204 272.639 625.611V625.607H272.639C272.647 624.074 272.422 623.182 272.119 622.75L272.115 622.744L272.115 622.744C271.895 622.424 271.478 622.126 270.87 621.909C270.27 621.694 269.546 621.58 268.805 621.58C268.064 621.58 267.34 621.694 266.74 621.909ZM260.992 634.843C261.695 634.229 262.604 633.883 263.539 633.883H274.07C275.016 633.883 275.916 634.23 276.618 634.843L276.636 634.859L276.654 634.877L276.671 634.895L277.449 635.575L277.45 635.576C278.283 636.309 278.762 637.358 278.762 638.465V641.347H277.262V638.465C277.262 637.795 276.972 637.154 276.459 636.703C276.459 636.703 276.459 636.703 276.459 636.703L275.665 636.007L275.647 635.991L275.63 635.974L275.613 635.958C275.182 635.588 274.638 635.383 274.07 635.383H263.539C262.973 635.383 262.414 635.593 261.981 635.971L261.163 636.7L261.155 636.706L261.155 636.706C260.649 637.146 260.356 637.791 260.356 638.465V641.347H258.856V638.465C258.856 637.363 259.332 636.304 260.169 635.576L260.987 634.848L260.992 634.843L260.992 634.843Z'}
              fillRule={'evenodd'}
            />
          </mask>

          <path
            d={'M273.349 621.892L273.35 622.642L273.349 621.142L273.349 621.892ZM264.259 622.645L273.35 622.642L273.349 621.142L264.258 621.145L264.259 622.645ZM264.258 622.645L273.35 622.642L273.349 621.142L264.258 621.145L264.258 622.645ZM273.349 621.892L272.114 622.743L272.12 622.751L273.349 621.892ZM271.375 620.496L271.88 619.084V619.084L271.375 620.496ZM266.235 620.496L265.73 619.084L265.73 619.084L266.235 620.496ZM274.139 625.613L272.639 625.605L272.639 625.613L274.139 625.613ZM273.351 629.406L274.587 630.257L274.587 630.256L273.351 629.406ZM273.351 629.407L274.587 630.257L274.587 630.257L273.351 629.407ZM271.359 630.78L271.837 632.201L271.359 630.78ZM268.78 631.148L268.804 629.649H268.804L268.78 631.148ZM266.215 630.705L265.704 632.116L265.704 632.116L266.215 630.705ZM264.259 629.328L265.494 628.477L265.494 628.477L264.259 629.328ZM264.256 629.324L263.404 628.089L262.17 628.94L263.02 630.175L264.256 629.324ZM264.256 629.324L265.107 630.559L266.332 629.714L265.497 628.482L264.256 629.324ZM265.495 622.744L266.731 623.593L266.731 623.593L265.495 622.744ZM266.74 621.909L267.245 623.321L266.74 621.909ZM265.494 622.744L266.731 623.594L266.731 623.594L265.494 622.744ZM265.496 628.48L264.252 629.319L264.257 629.326L264.262 629.333L265.496 628.48ZM266.726 629.295L266.215 630.705L266.215 630.705L266.726 629.295ZM268.804 629.649L268.828 628.149L268.804 629.649ZM270.88 629.358L271.359 630.78L271.359 630.78L270.88 629.358ZM272.115 628.556L273.35 629.407L273.351 629.406L272.115 628.556ZM272.639 625.607V624.107H271.139V625.607H272.639ZM272.639 625.607V627.107H274.13L274.139 625.615L272.639 625.607ZM272.119 622.75L273.347 621.889L273.347 621.889L272.119 622.75ZM272.115 622.744L271.267 621.507L270.016 622.364L270.887 623.605L272.115 622.744ZM272.115 622.744L272.963 623.981L274.201 623.132L273.352 621.895L272.115 622.744ZM270.87 621.909L270.364 623.321L270.87 621.909ZM260.992 634.843L259.862 635.83L260.849 636.96L261.979 635.973L260.992 634.843ZM276.618 634.843L275.631 635.973L275.631 635.973L276.618 634.843ZM276.636 634.859L277.695 633.797L277.66 633.762L277.623 633.73L276.636 634.859ZM276.654 634.877L275.595 635.939L275.595 635.939L276.654 634.877ZM276.671 634.895L275.613 635.957L275.647 635.991L275.683 636.023L276.671 634.895ZM277.449 635.575L276.46 636.704L276.46 636.704L277.449 635.575ZM277.45 635.576L278.441 634.45L278.438 634.448L277.45 635.576ZM278.762 641.347V642.847H280.262V641.347H278.762ZM277.262 641.347H275.762V642.847H277.262V641.347ZM276.459 636.703L275.466 637.827L275.468 637.829L276.459 636.703ZM276.459 636.703L277.452 635.579L277.447 635.574L276.459 636.703ZM275.665 636.007L274.677 637.135L274.677 637.135L275.665 636.007ZM275.647 635.991L274.588 637.054L274.622 637.088L274.658 637.119L275.647 635.991ZM275.63 635.974L276.689 634.912L276.688 634.912L275.63 635.974ZM275.613 635.958L276.672 634.895L276.632 634.856L276.59 634.819L275.613 635.958ZM261.981 635.971L260.996 634.84L260.99 634.845L260.984 634.85L261.981 635.971ZM261.163 636.7L260.165 635.579L260.165 635.579L261.163 636.7ZM261.155 636.706L260.02 637.686L261.015 638.84L262.153 637.826L261.155 636.706ZM261.155 636.706L262.291 635.726L261.309 634.588L260.173 635.573L261.155 636.706ZM260.356 641.347V642.847H261.856V641.347H260.356ZM258.856 641.347H257.356V642.847H258.856V641.347ZM260.169 635.576L261.153 636.708L261.16 636.702L261.166 636.697L260.169 635.576ZM260.987 634.848L261.984 635.968L261.984 635.968L260.987 634.848ZM260.992 634.843L262.122 633.857L261.125 632.716L259.994 633.723L260.992 634.843ZM274.585 621.041C273.881 620.019 272.828 619.423 271.88 619.084L270.87 621.909C271.478 622.126 271.894 622.423 272.114 622.743L274.585 621.041ZM271.88 619.084C270.903 618.735 269.829 618.58 268.805 618.58V621.58C269.546 621.58 270.27 621.694 270.87 621.909L271.88 619.084ZM268.805 618.58C267.781 618.58 266.707 618.735 265.73 619.084L266.74 621.909C267.34 621.694 268.064 621.58 268.805 621.58V618.58ZM265.73 619.084C264.78 619.424 263.726 620.021 263.022 621.045L265.495 622.744C265.714 622.424 266.131 622.127 266.74 621.909L265.73 619.084ZM275.639 625.622C275.648 623.988 275.449 622.277 274.579 621.032L272.12 622.751C272.423 623.185 272.647 624.076 272.639 625.605L275.639 625.622ZM274.587 630.256C275.441 629.014 275.639 627.278 275.639 625.614L272.639 625.613C272.639 627.204 272.413 628.123 272.115 628.556L274.587 630.256ZM274.587 630.257L274.587 630.257L272.116 628.556L272.115 628.556L274.587 630.257ZM271.837 632.201C272.791 631.88 273.871 631.297 274.587 630.257L272.115 628.556C271.903 628.865 271.492 629.152 270.88 629.358L271.837 632.201ZM268.756 632.648C269.776 632.665 270.854 632.532 271.837 632.201L270.88 629.358C270.279 629.56 269.55 629.661 268.804 629.649L268.756 632.648ZM265.704 632.116C266.668 632.465 267.734 632.632 268.756 632.648L268.804 629.649C268.058 629.637 267.328 629.513 266.726 629.295L265.704 632.116ZM263.023 630.179C263.721 631.192 264.775 631.779 265.704 632.116L266.726 629.295C266.111 629.073 265.701 628.778 265.494 628.477L263.023 630.179ZM263.02 630.175L263.023 630.179L265.494 628.477L265.491 628.473L263.02 630.175ZM263.404 628.089L263.404 628.089L265.107 630.559L265.107 630.559L263.404 628.089ZM261.971 625.611C261.971 627.253 262.176 628.93 263.014 630.166L265.497 628.482C265.194 628.034 264.971 627.142 264.971 625.611H261.971ZM263.022 621.045C262.173 622.28 261.971 623.98 261.971 625.611H264.971C264.971 624.078 265.195 623.181 265.494 622.744L263.022 621.045ZM266.731 623.593C266.715 623.616 266.729 623.587 266.816 623.528C266.902 623.469 267.04 623.395 267.245 623.321L266.235 620.496C265.455 620.775 264.72 621.223 264.258 621.895L266.731 623.593ZM266.731 623.594L266.731 623.593L264.258 621.895L264.258 621.895L266.731 623.594ZM266.471 625.611C266.471 624.911 266.523 624.406 266.592 624.058C266.664 623.694 266.74 623.58 266.731 623.594L264.258 621.895C263.684 622.731 263.471 624.03 263.471 625.611H266.471ZM266.739 627.64C266.739 627.641 266.663 627.519 266.591 627.159C266.522 626.811 266.471 626.309 266.471 625.611H263.471C263.471 627.194 263.684 628.477 264.252 629.319L266.739 627.64ZM267.237 627.885C267.026 627.808 266.885 627.732 266.799 627.674C266.71 627.614 266.704 627.59 266.729 627.626L264.262 629.333C264.714 629.987 265.444 630.426 266.215 630.705L267.237 627.885ZM268.828 628.149C268.219 628.139 267.658 628.037 267.237 627.885L266.215 630.705C266.998 630.989 267.896 631.134 268.78 631.148L268.828 628.149ZM270.402 627.936C269.991 628.074 269.437 628.159 268.828 628.149L268.78 631.148C269.663 631.163 270.567 631.046 271.359 630.78L270.402 627.936ZM270.88 627.706C270.904 627.67 270.9 627.692 270.82 627.745C270.741 627.797 270.607 627.867 270.402 627.936L271.359 630.78C272.142 630.516 272.886 630.081 273.35 629.407L270.88 627.706ZM271.139 625.611C271.139 626.343 271.086 626.871 271.016 627.233C270.942 627.615 270.864 627.728 270.879 627.707L273.351 629.406C273.664 628.952 273.848 628.389 273.961 627.804C274.078 627.201 274.139 626.472 274.139 625.611H271.139ZM271.139 625.607V625.611H274.139V625.607H271.139ZM272.639 624.107H272.639V627.107H272.639V624.107ZM270.891 623.611C270.878 623.593 270.953 623.7 271.025 624.058C271.093 624.4 271.143 624.901 271.139 625.599L274.139 625.615C274.148 624.032 273.936 622.728 273.347 621.889L270.891 623.611ZM270.887 623.605L270.891 623.611L273.347 621.889L273.343 621.883L270.887 623.605ZM271.267 621.507L271.267 621.507L272.963 623.981L272.963 623.981L271.267 621.507ZM270.364 623.321C270.57 623.395 270.708 623.469 270.793 623.528C270.881 623.587 270.894 623.616 270.878 623.593L273.352 621.895C272.89 621.223 272.155 620.775 271.375 620.496L270.364 623.321ZM268.805 623.08C269.404 623.08 269.953 623.174 270.364 623.321L271.375 620.496C270.587 620.215 269.687 620.08 268.805 620.08V623.08ZM267.245 623.321C267.656 623.174 268.206 623.08 268.805 623.08V620.08C267.922 620.08 267.023 620.215 266.235 620.496L267.245 623.321ZM263.539 632.383C262.236 632.383 260.977 632.864 260.005 633.714L261.979 635.973C262.412 635.594 262.972 635.383 263.539 635.383V632.383ZM274.07 632.383H263.539V635.383H274.07V632.383ZM277.605 633.714C276.635 632.866 275.386 632.383 274.07 632.383V635.383C274.646 635.383 275.197 635.593 275.631 635.973L277.605 633.714ZM277.623 633.73L277.605 633.714L275.631 635.973L275.649 635.989L277.623 633.73ZM277.712 633.814L277.695 633.797L275.577 635.922L275.595 635.939L277.712 633.814ZM277.73 633.832L277.712 633.814L275.595 635.939L275.613 635.957L277.73 633.832ZM278.437 634.447L277.66 633.766L275.683 636.023L276.46 636.704L278.437 634.447ZM278.438 634.448L278.437 634.447L276.46 636.704L276.462 636.705L278.438 634.448ZM280.262 638.465C280.262 636.922 279.593 635.465 278.441 634.45L276.459 636.703C276.972 637.154 277.262 637.795 277.262 638.465H280.262ZM280.262 641.347V638.465H277.262V641.347H280.262ZM277.262 642.847H278.762V639.847H277.262V642.847ZM275.762 638.465V641.347H278.762V638.465H275.762ZM275.468 637.829C275.661 637.999 275.762 638.231 275.762 638.465H278.762C278.762 637.359 278.283 636.31 277.45 635.577L275.468 637.829ZM275.466 637.827L275.466 637.827L277.453 635.579L277.452 635.579L275.466 637.827ZM274.677 637.135L275.471 637.831L277.447 635.574L276.653 634.879L274.677 637.135ZM274.658 637.119L274.677 637.135L276.653 634.879L276.635 634.863L274.658 637.119ZM274.571 637.037L274.588 637.054L276.706 634.929L276.689 634.912L274.571 637.037ZM274.555 637.02L274.571 637.037L276.688 634.912L276.672 634.895L274.555 637.02ZM274.07 636.883C274.273 636.883 274.472 636.955 274.637 637.097L276.59 634.819C275.892 634.221 275.003 633.883 274.07 633.883V636.883ZM263.539 636.883H274.07V633.883H263.539V636.883ZM262.967 637.102C263.13 636.959 263.34 636.883 263.539 636.883V633.883C262.606 633.883 261.698 634.228 260.996 634.84L262.967 637.102ZM262.16 637.82L262.979 637.091L260.984 634.85L260.165 635.579L262.16 637.82ZM262.153 637.826L262.16 637.82L260.165 635.579L260.158 635.586L262.153 637.826ZM260.02 637.686L260.02 637.686L262.291 635.726L262.291 635.726L260.02 637.686ZM261.856 638.465C261.856 638.221 261.964 637.991 262.138 637.84L260.173 635.573C259.334 636.3 258.856 637.361 258.856 638.465H261.856ZM261.856 641.347V638.465H258.856V641.347H261.856ZM258.856 642.847H260.356V639.847H258.856V642.847ZM257.356 638.465V641.347H260.356V638.465H257.356ZM259.184 634.444C258.016 635.46 257.356 636.934 257.356 638.465H260.356C260.356 637.792 260.648 637.147 261.153 636.708L259.184 634.444ZM259.989 633.728L259.171 634.456L261.166 636.697L261.984 635.968L259.989 633.728ZM259.994 633.723L259.989 633.728L261.984 635.968L261.989 635.963L259.994 633.723ZM262.122 633.857L262.122 633.857L259.862 635.83L259.862 635.83L262.122 633.857Z'}
            fill={'#667281'}
            mask={'url(#path-65-inside-2_1200_53683)'}
          />
        </g>
      </svg>
    </SvgWrapper>
  );
}
